import { format } from 'date-fns';
import { getShopHoursForDay, ShopHours } from './shopHoursUtils';

const DAY_NAMES = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
] as const;

function shopHasAnyConfiguredDay(shopHours: ShopHours): boolean {
  for (const day of DAY_NAMES) {
    const lower = shopHours[day];
    if (lower?.open && lower?.close) return true;
    const capital = day.charAt(0).toUpperCase() + day.slice(1);
    const cap = (shopHours as Record<string, unknown>)[capital];
    if (
      cap &&
      typeof cap === 'object' &&
      (cap as { open?: string; close?: string }).open &&
      (cap as { open?: string; close?: string }).close
    ) {
      return true;
    }
  }
  return false;
}

/**
 * Parse the `ticketData.appointmentDate` string OptiPlaceModal receives
 * into a noon-anchored Date in browser-local time.
 *
 * Accepts either:
 *   - bare day labels:  "YYYY-MM-DD"
 *   - ISO-ish strings:  "YYYY-MM-DDTHH:mm:ss" / "YYYY-MM-DDTHH:mm"
 *
 * The noon anchor mirrors DateContext.selectedDate (DateContext.tsx:84):
 * it sidesteps DST / TZ-boundary issues for calendar-display Dates while
 * still representing the chosen shop-local day.
 *
 * Returns null when the input is missing or unparseable so callers can
 * fall back to a context-derived date.
 */
export function parseAppointmentDateAsDate(
  appointmentDate: string | undefined | null,
): Date | null {
  if (!appointmentDate) return null;
  const ymd = appointmentDate.includes('T')
    ? appointmentDate.slice(0, 10)
    : appointmentDate;
  const parts = ymd.split('-').map(Number);
  if (parts.length < 3) return null;
  const [y, m, d] = parts;
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return null;
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d, 12, 0, 0);
}

export interface OptiFinishAdvisory {
  /**
   * True when the tech's predicted finish lands after the shop's close
   * on the finish day — OR when the shop is closed that day entirely
   * (any work that day is by definition past close).
   */
  willRunPastClose: boolean;
  /** Pre-formatted finish-time label for advisory copy (e.g. "5:45 PM"). */
  finishLabel: string;
  /**
   * Pre-formatted close-time label for advisory copy (e.g. "5:00 PM").
   * Empty string when the shop has no configured close that day —
   * callers should switch to the closed-day copy variant in that case.
   */
  closeLabel: string;
  /**
   * True when the shop has no configured hours for the finish day
   * (i.e., the tech is working on a shop-closed day). Callers use
   * this to swap copy to "shop is normally closed this day" wording.
   */
  shopClosedThatDay: boolean;
}

interface AdvisoryRankingLike {
  suggestedFinishTime?: string | null;
  isRollover?: boolean;
  willRollover?: boolean;
}

/**
 * Compute the past-close advisory for a single OptiPlace ranking row.
 *
 * Returns null only when there's nothing to compare:
 *   - no concrete `suggestedFinishTime` on the ranking, OR
 *   - shop hours haven't loaded yet, OR
 *   - shop hours are fully empty / unconfigured (we don't fire
 *     speculative warnings while the operator is still building the
 *     shop profile).
 *
 * Otherwise returns a structured advisory. Importantly:
 *   - The rollover flags do NOT suppress the advisory. A tech the smart-
 *     assign function marked as `willRollover` may still finish past close
 *     in a way the operator should see; if the OptiPlace card also renders
 *     a "Rollover Required" badge, the two carry complementary info
 *     ("work is split" vs "finish is after hours") and can coexist.
 *   - Closed-day finishes (shop has hours generally, but not for the
 *     specific finish day) set `shopClosedThatDay: true` and
 *     `willRunPastClose: true` so the advisory fires for techs working
 *     weekends on a Mon-Fri shop.
 *   - Ranking order, score, and visibility are never touched.
 */
export function computeOptiFinishAdvisory(
  ranking: AdvisoryRankingLike,
  shopHours: ShopHours | null | undefined,
  timezone: string | null | undefined,
): OptiFinishAdvisory | null {
  if (!ranking.suggestedFinishTime || !shopHours || !timezone) return null;

  const finishDate = new Date(ranking.suggestedFinishTime);
  if (Number.isNaN(finishDate.getTime())) return null;

  if (!shopHasAnyConfiguredDay(shopHours)) return null;

  const finishLabel = format(finishDate, 'h:mm a');
  const hours = getShopHoursForDay(finishDate, shopHours, timezone);

  // Shop has hours generally, but not on this specific finish day —
  // treat as "closed that day, any work is past close."
  if (!hours?.close) {
    return {
      willRunPastClose: true,
      finishLabel,
      closeLabel: '',
      shopClosedThatDay: true,
    };
  }

  const [closeHourRaw, closeMinuteRaw] = String(hours.close).split(':');
  const closeHour = Number(closeHourRaw);
  const closeMinute = Number(closeMinuteRaw ?? 0);
  if (!Number.isFinite(closeHour) || !Number.isFinite(closeMinute)) return null;

  const finishHour = finishDate.getHours();
  const finishMinute = finishDate.getMinutes();

  const finishTotal = finishHour * 60 + finishMinute;
  const closeTotal = closeHour * 60 + closeMinute;

  return {
    willRunPastClose: finishTotal > closeTotal,
    finishLabel,
    closeLabel: format(
      new Date(2000, 0, 1, closeHour, closeMinute, 0),
      'h:mm a',
    ),
    shopClosedThatDay: false,
  };
}
